import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, GstOverviewProps, GstOverviewGstinRow } from '../data/types';
import { ScreenSection, ScreenDivider } from '../primitives/ScreenPrimitives';
import { colors, coverFonts, overviewColors, ptToPx, space } from '../data/designTokens';
import { chartColors } from '../primitives/GstChartPrimitives';

// ════════════════════════════════════════════════════════════════
//  GST Overview — KPI strip, annual turnover range chart,
//  state coverage summary, and GSTIN details table.
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;
const DASH = '—';
const BAR_COLOR = chartColors.purple;

const getData = (data: ReportData): GstOverviewProps | undefined => data.gstOverview;

// ── Formatting helpers ────────────────────────────────────────

function formatFyShort(fy: string): string {
    const m = fy?.match(/^(\d{4})-(\d{4})$/);
    if (!m) return fy || '';
    return `FY'${m[1].slice(-2)}-${m[2].slice(-2)}`;
}

// ── KPI Strip ──────────────────────────────────────────────────

const kpiCard = {
    bg: colors.paper,
    border: `1px solid ${overviewColors.border}`,
    radius: ptToPx(4),
    padX: ptToPx(12),
    padY: ptToPx(9),
} as const;

const ScreenKpiStrip: React.FC<{ d: GstOverviewProps }> = ({ d }) => (
    <div style={{ display: 'flex', gap: ptToPx(8), marginTop: ptToPx(space.sp6) }}>
        <div
            style={{
                flex: 1,
                background: kpiCard.bg,
                border: kpiCard.border,
                borderRadius: kpiCard.radius,
                padding: `${kpiCard.padY}px ${kpiCard.padX}px`,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: ptToPx(7.5), color: overviewColors.inkMuted, fontFamily: SF.sans }}>Total GSTIN</span>
                <span
                    style={{
                        fontSize: ptToPx(6.5),
                        color: overviewColors.inkSoft,
                        fontFamily: SF.sans,
                        background: overviewColors.surface,
                        padding: `${ptToPx(1.5)}px ${ptToPx(5)}px`,
                        borderRadius: ptToPx(8),
                    }}
                >
                    {d.totalGstinCount} Total
                </span>
            </div>
            <div style={{ marginTop: ptToPx(4), fontSize: ptToPx(16), fontWeight: 600, color: overviewColors.ink, fontFamily: SF.sans }}>
                {d.activeGstinCount}
                <span style={{ fontSize: ptToPx(8), fontWeight: 400, color: overviewColors.inkMuted, marginLeft: ptToPx(4) }}>Active</span>
            </div>
        </div>
        <div
            style={{
                flex: 1,
                background: kpiCard.bg,
                border: kpiCard.border,
                borderRadius: kpiCard.radius,
                padding: `${kpiCard.padY}px ${kpiCard.padX}px`,
            }}
        >
            <span style={{ fontSize: ptToPx(7.5), color: overviewColors.inkMuted, fontFamily: SF.sans }}>HSN &amp; SAC Active</span>
            <div style={{ marginTop: ptToPx(4), fontSize: ptToPx(16), fontWeight: 600, color: overviewColors.ink, fontFamily: SF.sans }}>
                {d.hsnSacActiveCount}
            </div>
        </div>
    </div>
);

// ── Annual Turnover Range Chart (SVG) ──────────────────────────

const ScreenTurnoverChart: React.FC<{ d: GstOverviewProps }> = ({ d }) => {
    const { yoYTurnover, latestFyTurnoverBand, latestFy } = d;
    if (!yoYTurnover || yoYTurnover.length === 0) return null;

    const chartData = yoYTurnover.map((e) => ({
        fy: formatFyShort(e.financialYear),
        band: e.turnoverBand,
        value: e.slabValue,
    }));

    const maxVal = Math.max(...chartData.map((c) => c.value), 1);
    const w = 620;
    const h = 160;
    const barAreaTop = 40;
    const barAreaBottom = h - 20;
    const barAreaH = barAreaBottom - barAreaTop;
    const barCount = chartData.length;
    const barW = Math.min(46, (w - 60) / barCount - 8);

    return (
        <div
            style={{
                marginTop: ptToPx(8),
                background: colors.paper,
                border: `1px solid ${overviewColors.border}`,
                borderRadius: ptToPx(4),
                padding: `${ptToPx(12)}px ${ptToPx(14)}px`,
            }}
        >
            <div style={{ fontSize: ptToPx(8), fontWeight: 600, color: overviewColors.ink, fontFamily: SF.sans }}>
                Annual Turnover Range
            </div>
            <div style={{ marginTop: ptToPx(4), display: 'flex', alignItems: 'baseline', gap: ptToPx(6) }}>
                <span style={{ fontSize: ptToPx(12), fontWeight: 700, color: overviewColors.ink, fontFamily: SF.sans }}>
                    {latestFyTurnoverBand || DASH}
                </span>
                <span style={{ fontSize: ptToPx(7), color: overviewColors.inkMuted, fontFamily: SF.sans }}>
                    In {formatFyShort(latestFy)}
                </span>
            </div>
            <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block', marginTop: ptToPx(6) }}>
                {/* Grid lines */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
                    const y = barAreaBottom - pct * barAreaH;
                    return (
                        <line
                            key={pct}
                            x1={30}
                            y1={y}
                            x2={w - 10}
                            y2={y}
                            stroke={chartColors.gridLine}
                            strokeDasharray="4 4"
                            strokeWidth={1}
                        />
                    );
                })}
                {chartData.map((d, i) => {
                    const x = 40 + i * ((w - 80) / barCount) + ((w - 80) / barCount - barW) / 2;
                    const barH = (d.value / maxVal) * barAreaH;
                    const y = barAreaBottom - barH;
                    return (
                        <g key={i}>
                            <rect x={x} y={y} width={barW} height={barH} rx={4} fill={BAR_COLOR} />
                            <text x={x + barW / 2} y={y - 8} fontSize={9} fill={chartColors.ink} textAnchor="middle" fontWeight={600}>
                                {d.band}
                            </text>
                            <text x={x + barW / 2} y={barAreaBottom + 14} fontSize={9} fill={chartColors.axisLabel} textAnchor="middle">
                                {d.fy}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

// ── GSTIN Details Table ────────────────────────────────────────

const tbl = {
    headerBg: colors.surface,
    headerSize: ptToPx(7),
    headerColor: colors.ink40,
    cellSize: ptToPx(7.5),
    cellColor: colors.ink20,
    cellPad: `${ptToPx(6)}px ${ptToPx(8)}px`,
    border: `0.5px solid ${overviewColors.border}`,
} as const;

const STATUS_ACTIVE = 'active';
const STATUS_INACTIVE = 'inactive';
const MAX_GSTIN_ROWS = 50;

function resolveStatus(status: string): { color: string; bg: string } {
    const s = (status || '').toLowerCase().trim();
    if (s === STATUS_ACTIVE) return { color: colors.pos, bg: colors.posSoft };
    if (s === STATUS_INACTIVE) return { color: colors.ink40, bg: colors.neutralSoft };
    return { color: colors.ink40, bg: colors.neutralSoft };
}

type StateGroup = {
    state: string;
    total: number;
    active: number;
    rows: GstOverviewGstinRow[];
};

function groupByState(rows: GstOverviewGstinRow[]): StateGroup[] {
    const map = new Map<string, GstOverviewGstinRow[]>();
    for (const r of rows) {
        const key = r.state || 'Unknown';
        const list = map.get(key) || [];
        list.push(r);
        map.set(key, list);
    }
    return Array.from(map.entries())
        .map(([state, stateRows]) => ({
            state,
            total: stateRows.length,
            active: stateRows.filter((r) => (r.status || '').toLowerCase() === STATUS_ACTIVE).length,
            rows: stateRows,
        }))
        .sort((a, b) => b.total - a.total);
}

const ScreenGstinTable: React.FC<{ rows: GstOverviewGstinRow[] }> = ({ rows }) => {
    if (rows.length === 0) return null;
    const capped = rows.slice(0, MAX_GSTIN_ROWS);
    const groups = groupByState(capped);
    const isCapped = rows.length > MAX_GSTIN_ROWS;
    return (
        <div style={{ marginTop: ptToPx(space.sp8) }}>
            <ScreenDivider />
            <div style={{ marginTop: ptToPx(space.sp6) }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: ptToPx(6) }}>
                    <div style={{ fontSize: ptToPx(9), fontWeight: 600, color: overviewColors.ink, fontFamily: SF.sans }}>GST Details</div>
                    {isCapped && (
                        <span style={{ fontSize: ptToPx(7), color: overviewColors.inkMuted, fontFamily: SF.sans }}>
                            Showing top {MAX_GSTIN_ROWS} of {rows.length}
                        </span>
                    )}
                </div>
                <div
                    style={{
                        marginTop: ptToPx(6),
                        border: `1px solid ${overviewColors.border}`,
                        borderRadius: ptToPx(4),
                        overflow: 'hidden',
                    }}
                >
                    {/* Column Header */}
                    <div
                        style={{
                            display: 'flex',
                            background: tbl.headerBg,
                            borderBottom: tbl.border,
                            fontSize: tbl.headerSize,
                            fontWeight: 600,
                            color: tbl.headerColor,
                            fontFamily: SF.sans,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        <div style={{ flex: 2, padding: tbl.cellPad }}>GSTIN</div>
                        <div style={{ flex: 1.5, padding: tbl.cellPad }}>Trade Name</div>
                        <div style={{ flex: 2, padding: tbl.cellPad }}>Nature of Business</div>
                        <div style={{ flex: 2.5, padding: tbl.cellPad }}>HSN &amp; SAC</div>
                        <div style={{ flex: 2.5, padding: tbl.cellPad }}>Address</div>
                    </div>
                    {/* Grouped rows */}
                    {groups.map((group, gi) => (
                        <React.Fragment key={group.state}>
                            {/* State header row */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: ptToPx(6),
                                    padding: `${ptToPx(5)}px ${ptToPx(8)}px`,
                                    background: overviewColors.surface,
                                    borderBottom: tbl.border,
                                    borderTop: gi > 0 ? tbl.border : undefined,
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: ptToPx(7.5),
                                        fontWeight: 600,
                                        color: overviewColors.ink,
                                        fontFamily: SF.sans,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    {group.state}
                                </span>
                                <span
                                    style={{
                                        fontSize: ptToPx(6.5),
                                        color: overviewColors.inkMuted,
                                        fontFamily: SF.sans,
                                    }}
                                >
                                    {group.active} Active / {group.total} Total
                                </span>
                            </div>
                            {/* GSTIN rows within this state */}
                            {group.rows.map((row, i) => (
                                <div
                                    key={row.gstin || i}
                                    style={{
                                        display: 'flex',
                                        borderBottom: i < group.rows.length - 1 ? tbl.border : undefined,
                                        fontSize: tbl.cellSize,
                                        color: tbl.cellColor,
                                        fontFamily: SF.sans,
                                    }}
                                >
                                    <div style={{ flex: 2, padding: tbl.cellPad }}>
                                        <div
                                            style={{
                                                fontFamily: SF.mono,
                                                fontSize: ptToPx(7),
                                                color: overviewColors.ink,
                                            }}
                                        >
                                            {row.gstin}
                                        </div>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                marginTop: ptToPx(2),
                                                fontSize: ptToPx(6),
                                                fontWeight: 500,
                                                ...resolveStatus(row.status),
                                                padding: `${ptToPx(1)}px ${ptToPx(4)}px`,
                                                borderRadius: ptToPx(6),
                                            }}
                                        >
                                            {row.status}
                                        </span>
                                    </div>
                                    <div
                                        style={{
                                            flex: 1.5,
                                            padding: tbl.cellPad,
                                            color: row.tradeName ? tbl.cellColor : overviewColors.inkMuted,
                                        }}
                                    >
                                        {row.tradeName || DASH}
                                    </div>
                                    <div style={{ flex: 2, padding: tbl.cellPad }}>
                                        {row.natureOfBusiness.length > 0
                                            ? row.natureOfBusiness.slice(0, 3).map((nob, j) => (
                                                  <div key={j} style={{ fontSize: ptToPx(6.5), lineHeight: 1.4 }}>
                                                      {nob}
                                                  </div>
                                              ))
                                            : DASH}
                                    </div>
                                    <div style={{ flex: 2.5, padding: tbl.cellPad }}>
                                        {row.hsn.length > 0 || row.sac.length > 0
                                            ? [...row.hsn.slice(0, 2), ...row.sac.slice(0, 2)].slice(0, 3).map((item, j) => (
                                                  <div key={j} style={{ fontSize: ptToPx(6.5), lineHeight: 1.4 }}>
                                                      {item.description || item.code}
                                                  </div>
                                              ))
                                            : DASH}
                                    </div>
                                    <div
                                        style={{
                                            flex: 2.5,
                                            padding: tbl.cellPad,
                                            fontSize: ptToPx(6.5),
                                            lineHeight: 1.4,
                                            color: row.address ? tbl.cellColor : overviewColors.inkMuted,
                                        }}
                                    >
                                        {row.address || DASH}
                                    </div>
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ── Screen Renderer ────────────────────────────────────────────

const ScreenGstOverview: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const d = getData(data);
    if (!d) return null;
    return (
        <ScreenSection
            kicker="GST"
            title="GST Overview"
            subtitle={`GSTIN registration details, turnover range & state coverage · Source: GST Portal`}
            index={index}
        >
            <ScreenKpiStrip d={d} />
            {d.signatories.length > 0 && (
                <div
                    style={{
                        marginTop: ptToPx(6),
                        display: 'flex',
                        alignItems: 'center',
                        gap: ptToPx(6),
                        fontSize: ptToPx(7.5),
                        fontFamily: SF.sans,
                    }}
                >
                    <span style={{ color: overviewColors.inkMuted }}>Signatories</span>
                    <div style={{ display: 'flex', gap: ptToPx(4), flexWrap: 'wrap' }}>
                        {d.signatories.map((name, i) => (
                            <span
                                key={i}
                                style={{
                                    background: overviewColors.surface,
                                    border: `1px solid ${overviewColors.border}`,
                                    borderRadius: ptToPx(8),
                                    padding: `${ptToPx(2)}px ${ptToPx(8)}px`,
                                    fontSize: ptToPx(7),
                                    color: overviewColors.inkSoft,
                                    fontWeight: 500,
                                }}
                            >
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <ScreenTurnoverChart d={d} />
            <ScreenGstinTable rows={d.rows} />
        </ScreenSection>
    );
};

// ════════════════════════════════════════════════════════════════
//                          M O D U L E   D E F
// ════════════════════════════════════════════════════════════════

export const gstOverviewModule: ModuleDefinition = {
    meta: {
        id: 'gstOverview',
        title: 'GST Overview',
        description: 'GSTIN registration details, annual turnover range, state coverage, and GST details table.',
        dataSource: 'GST Portal',
    },
    hasData: (d) => !!d.gstOverview && (d.gstOverview.totalGstinCount > 0 || d.gstOverview.rows.length > 0),
    Screen: ScreenGstOverview,
};
